import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Alert, Button, Container, Field, Input, Panel } from '../components/site/ui';
import { IconArrowLeft, IconMail } from '../components/icons';
import SiteCredit from '../components/SiteCredit';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  /**
   * The API answers identically whether or not the address exists, so this
   * screen does too — showing "no such account" here would undo the server's
   * protection against account enumeration.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/api/auth/forgot-password', { email: email.trim() });
    } catch {
      /* deliberately ignored: the outcome must look the same either way */
    } finally {
      setLoading(false);
      setSent(true);
    }
  };

  return (
    <Container width="sm" className="py-12 sm:py-16">
      <div className="mb-8">
        <Link to="/" className="inline-block w-fit">
          <img src="/brand.png" alt="ParcelBuddy" className="h-16 w-auto" />
        </Link>
      </div>

      <Panel>
        {sent ? (
          <>
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-100">
              <IconMail size={22} />
            </span>
            <h1 className="mt-5 font-display text-xl font-700 tracking-tight text-ink-900">
              Check your email
            </h1>
            <p className="mt-2 text-[15px] leading-relaxed text-ink-600">
              If that email address has an account, a reset link is on its way. The link expires
              shortly and can only be used once.
            </p>
            <Alert tone="info" className="mt-5">
              Nothing arrived? Check your spam folder, or ask your ParcelBuddy administrator to send
              you a fresh invitation.
            </Alert>
            <Button
              variant="secondary"
              className="mt-6"
              onClick={() => {
                setSent(false);
                setEmail('');
              }}
            >
              Try a different address
            </Button>
          </>
        ) : (
          <>
            <h1 className="font-display text-xl font-700 tracking-tight text-ink-900">
              Forgot your password?
            </h1>
            <p className="mt-2 text-[15px] leading-relaxed text-ink-600">
              Enter your email address and we&apos;ll send you a link to set a new one.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5" noValidate>
              <Field label="Email address" htmlFor="email" required>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourcompany.co.uk"
                  autoComplete="username"
                  required
                  autoFocus
                />
              </Field>

              <Button type="submit" size="lg" loading={loading} className="w-full">
                Send reset link
              </Button>
            </form>
          </>
        )}
      </Panel>

      <p className="mt-6 text-center">
        <Link
          to="/merchant/login"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:text-brand-800 hover:underline"
        >
          <IconArrowLeft size={15} />
          Back to sign in
        </Link>
      </p>

      <SiteCredit className="mt-4 text-center" />
    </Container>
  );
};

export default ForgotPasswordPage;
